<script lang="ts">
  import type { AppContext, MissionInfo } from '../app';
  import { lang, t, type MessageKey } from '../i18n';

  // Fact sheet of a mission (S3-16, Concept §4.3): agencies, launch, end, status, outcome, targets, spacecraft and,
  // for crewed missions, the crew (S4-10: each person once, in the order of the spacecraft).
  let { ctx, mission }: { ctx: AppContext; mission: MissionInfo } = $props();

  const fmtDay = (iso: string, l: string): string =>
    new Intl.DateTimeFormat(l === 'de' ? 'de-DE' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(Date.parse(iso.length === 10 ? `${iso}T00:00:00Z` : iso));

  const d = $derived(mission.data);
  const targets = $derived(d.targets.map((id) => ctx.targets.get(id)?.name[$lang] ?? id).join(', '));
  const crew = $derived(
    d.spacecraft
      .flatMap((sc) => sc.crew ?? [])
      .filter((c, i, all) => all.findIndex((x) => x.name === c.name) === i)
      .map((c) => `${c.name} (${c.role[$lang]})`),
  );
</script>

<dl class="facts">
  <div>
    <dt class="caps-label">{$t('fact.agencies')}</dt>
    <dd>{mission.agencies.map((a) => a.name[$lang]).join(', ')}</dd>
  </div>
  <div>
    <dt class="caps-label">{$t('fact.launch')}</dt>
    <dd>
      {fmtDay(d.launch.date, $lang)}
      {#if d.launch.vehicle || d.launch.site}
        <span class="muted">· {[d.launch.vehicle, d.launch.site].filter(Boolean).join(', ')}</span>
      {/if}
    </dd>
  </div>
  <div>
    <dt class="caps-label">{$t('fact.end')}</dt>
    <dd>{d.end ? fmtDay(d.end, $lang) : '—'}</dd>
  </div>
  <div>
    <dt class="caps-label">{$t('fact.status')}</dt>
    <dd>
      {$t(`status.${d.status}` as MessageKey)}{#if d.outcome}, {$t(`outcome.${d.outcome}` as MessageKey)}{/if}
    </dd>
  </div>
  {#if targets}
    <div>
      <dt class="caps-label">{$t('fact.targets')}</dt>
      <dd>{targets}</dd>
    </div>
  {/if}
  <div>
    <dt class="caps-label">{$t('fact.spacecraft')}</dt>
    <dd>
      {d.spacecraft.map((sc) => `${sc.name ?? d.name[$lang]} (${$t(`type.${sc.type}` as MessageKey)})`).join(', ')}
    </dd>
  </div>
  {#if crew.length}
    <div class="wide" data-testid="fact-crew">
      <dt class="caps-label">{$t('fact.crew')}</dt>
      <dd>{crew.join(', ')}</dd>
    </div>
  {/if}
</dl>

<style>
  .facts {
    margin: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
  }
  .facts > div {
    min-width: 0;
  }
  .facts > .wide {
    grid-column: 1 / -1;
  }
  dd {
    margin: 2px 0 0;
    font-size: 0.85rem;
  }
  .muted {
    color: var(--fg-muted);
  }
</style>
